import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import API from "../../api/api";
import Pagination from "../../component/Pagination.js";
import {
  Search,
  ExternalLink,
  GraduationCap,
  Building2,
  X,
  Loader2,
  CheckCircle2,
  Globe,
  Sparkles,
  Calendar,
} from "lucide-react";

function Result() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const mockUniversityResults = [
    {
      _id: "res_u1",
      University: "Bansal Institute of Engineering & Tech (BIET)",
      link: "https://result.biet.ac.in/semester-results-2026",
      createdAt: "2026-08-20T10:00:00.000Z",
    },
    {
      _id: "res_u2",
      University: "Deen Dayal Upadhyaya Gorakhpur University (DDU)",
      link: "https://ddugu.ac.in/examination-results.aspx",
      createdAt: "2026-08-18T14:30:00.000Z",
    },
    {
      _id: "res_u3",
      University: "University of Delhi (DU - South & North Campus)",
      link: "https://slc.uod.ac.in/index.php/site/login",
      createdAt: "2026-08-15T09:15:00.000Z",
    },
    {
      _id: "res_u4",
      University: "Dr. A.P.J. Abdul Kalam Technical University (AKTU)",
      link: "https://oneview.aktu.ac.in/",
      createdAt: "2026-08-12T11:45:00.000Z",
    },
    {
      _id: "res_u5",
      University: "Vellore Institute of Technology (VIT Vellore)",
      link: "https://vtop.vit.ac.in/vtop/initialProcess",
      createdAt: "2026-08-10T16:20:00.000Z",
    },
    {
      _id: "res_u6",
      University: "BITS Pilani (Birla Institute of Technology and Science)",
      link: "https://www.bitsadmission.com/results",
      createdAt: "2026-08-05T08:00:00.000Z",
    },
  ];

  // Fetch all results on mount & page change
  useEffect(() => {
    const fetchAllResults = async () => {
      try {
        setInitialLoading(true);
        const response = await API.get(`/result/showall?page=${page}&limit=20`);
        if (
          response.data &&
          response.data.showall &&
          response.data.showall.length > 0
        ) {
          setAllResults([...response.data.showall, ...mockUniversityResults]);
        } else {
          setAllResults(mockUniversityResults);
        }
      } catch (error) {
        console.warn("API unavailable, fallback to mock university links");
        setAllResults(mockUniversityResults);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchAllResults();
  }, [page]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setShowDropdown(true);
    const delay = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await API.get(
          `/result/searchResultQuery?university=${encodeURIComponent(query)}`
        );
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          setSearchResults(response.data.results);
        } else {
          const filtered = allResults.filter((u) =>
            u.University.toLowerCase().includes(query.toLowerCase())
          );
          setSearchResults(filtered);
        }
      } catch (err) {
        const filtered = allResults.filter((u) =>
          u.University.toLowerCase().includes(query.toLowerCase())
        );
        setSearchResults(filtered);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(delay);
  }, [query, allResults]);

  // Handle result click
  const handleResultClick = (link) => {
    if (!link) {
      toast.error("No result link available");
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleClear = () => {
    setQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 pb-16">
      {/* Hero Portal Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white py-12 px-4 shadow-sm">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center space-x-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold text-purple-200 mb-3 border border-white/10">
            <GraduationCap size={15} />
            <span>Official University Result Portal</span>
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
            Exam & Semester Results
          </h1>
          <p className="text-sm sm:text-base text-purple-100 max-w-xl mx-auto font-medium mb-8">
            Access official university result portals, marksheet downloads, and grade notifications instantly.
          </p>

          {/* Search Box in Hero */}
          <div className="relative max-w-2xl mx-auto text-left">
            <div className="relative">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search university (e.g. DDU, AKTU, DU, BIET, BITS)..."
                className="w-full pl-11 pr-10 py-3.5 bg-white rounded-2xl shadow-lg text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-400 border border-transparent transition"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => query && setShowDropdown(true)}
                onBlur={handleBlur}
              />
              {query && (
                <button
                  onClick={handleClear}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Live Autocomplete Dropdown */}
            {showDropdown && (
              <div className="absolute w-full mt-2 border border-gray-100 rounded-2xl bg-white shadow-2xl max-h-80 overflow-y-auto z-20 divide-y divide-gray-100">
                {loading ? (
                  <div className="p-5 text-center flex items-center justify-center space-x-2 text-gray-500">
                    <Loader2 size={18} className="animate-spin text-purple-600" />
                    <span className="text-xs font-semibold">
                      Searching university portals...
                    </span>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Building2 className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="font-bold text-sm text-gray-800">
                      No matching university found
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Check your spelling or search for university abbreviation
                    </p>
                  </div>
                ) : (
                  searchResults.map((item) => (
                    <div
                      key={item._id}
                      onClick={() => handleResultClick(item.link)}
                      className="p-4 hover:bg-purple-50/70 transition cursor-pointer flex items-center justify-between group"
                    >
                      <div className="flex items-center space-x-3 min-w-0 pr-2">
                        <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs shrink-0">
                          {item.University.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-sm text-gray-900 group-hover:text-purple-700 transition-colors truncate">
                            {item.University}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {item.link}
                          </p>
                        </div>
                      </div>
                      <ExternalLink
                        size={16}
                        className="text-gray-400 group-hover:text-purple-600 transition-colors shrink-0"
                      />
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-8">
        {/* Quick Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-xs border border-gray-100/90 flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <Building2 size={24} />
            </div>
            <div>
              <span className="text-2xl font-extrabold text-gray-900 block leading-none">
                {allResults.length}+
              </span>
              <span className="text-xs font-semibold text-gray-500">
                Universities Indexed
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-xs border border-gray-100/90 flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold">
              <Globe size={24} />
            </div>
            <div>
              <span className="text-2xl font-extrabold text-gray-900 block leading-none">
                24/7
              </span>
              <span className="text-xs font-semibold text-gray-500">
                Direct Portal Links
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-xs border border-gray-100/90 flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <Sparkles size={24} />
            </div>
            <div>
              <span className="text-2xl font-extrabold text-gray-900 block leading-none">
                Real-Time
              </span>
              <span className="text-xs font-semibold text-gray-500">
                Result Notifications
              </span>
            </div>
          </div>
        </div>

        {/* All University Results Cards Grid */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/90">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                All University Result Links
              </h2>
              <p className="text-xs text-gray-500 mt-0.5 font-medium">
                Click any portal to check semester marksheets & result announcements
              </p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-100">
              {allResults.length} Portals
            </span>
          </div>

          {initialLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
              <p className="text-gray-500 text-sm font-medium">
                Loading result links...
              </p>
            </div>
          ) : allResults.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-bold text-gray-800 text-base">
                No university result links available
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {allResults.map((item) => (
                <div
                  key={item._id}
                  onClick={() => handleResultClick(item.link)}
                  className="group bg-slate-50/60 hover:bg-white border border-gray-200/80 hover:border-purple-300 rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-xs shrink-0">
                        {item.University.charAt(0)}
                      </div>
                      <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                        <CheckCircle2 size={12} className="text-blue-600" />
                        <span>Verified</span>
                      </span>
                    </div>

                    <h3 className="font-bold text-gray-900 text-base group-hover:text-purple-700 transition-colors line-clamp-2 mb-2 leading-snug">
                      {item.University}
                    </h3>

                    <p className="text-xs text-gray-400 truncate mb-4 font-mono bg-white p-2 rounded-lg border border-gray-100">
                      {item.link}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center space-x-1 font-medium">
                      <Calendar size={13} className="text-gray-400" />
                      <span>
                        Updated:{" "}
                        {new Date(
                          item.createdAt || Date.now()
                        ).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </span>

                    <span className="font-bold text-purple-600 group-hover:translate-x-0.5 transition-transform flex items-center space-x-1">
                      <span>View Result</span>
                      <ExternalLink size={14} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="mt-8">
            <Pagination onPageChange={setPage} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Result;
